import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../Styles/Auth.css';
import { useUser } from '../Context/UserContext';
import ReactCountryFlag from 'react-country-flag';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const Auth = () => {
    const { user, login } = useUser();
    const [isLogin, setIsLogin] = useState(true);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        country: 'Lebanon',
        country_code: '+961',
        city: '',
        phone_number: '',
        address1: '',
        address2: '',
        street: '',
        building: '',
        floor: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isCountryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const [deliveryCities, setDeliveryCities] = useState([]);
    const [showOtherCityInput, setShowOtherCityInput] = useState(false);
    const countryDropdownRef = useRef(null);

    const countryOptions = [
        { value: '+1', label: 'United States', iso: 'US' },
        { value: '+44', label: 'United Kingdom', iso: 'GB' },
        { value: '+91', label: 'India', iso: 'IN' },
        { value: '+971', label: 'UAE', iso: 'AE' },
        { value: '+20', label: 'Egypt', iso: 'EG' },
        { value: '+965', label: 'Kuwait', iso: 'KW' },
        { value: '+974', label: 'Qatar', iso: 'QA' },
        { value: '+973', label: 'Bahrain', iso: 'BH' },
        { value: '+968', label: 'Oman', iso: 'OM' },
        { value: '+962', label: 'Jordan', iso: 'JO' },
        { value: '+961', label: 'Lebanon', iso: 'LB' },
        { value: '+963', label: 'Syria', iso: 'SY' },
        { value: '+964', label: 'Iraq', iso: 'IQ' },
        { value: '+967', label: 'Yemen', iso: 'YE' }
    ];

    const getCountryByCode = (countryCode) => {
        return countryOptions.find((option) => option.value === countryCode) || countryOptions[0];
    };

    const getPhoneMaxLength = (countryCode) => {
        const phoneMaxLengths = {
            '+1': 10,
            '+44': 10,
            '+91': 10,
            '+971': 9,
            '+966': 9,
            '+20': 10,
            '+965': 8,
            '+974': 8,
            '+973': 8,
            '+968': 8,
            '+962': 9,
            '+961': 8,
            '+963': 9,
            '+964': 10,
            '+967': 8
        };
        return phoneMaxLengths[countryCode] || 15;
    };

    const sanitizePhoneNumber = (value, countryCode) => {
        const digits = value.replace(/\D/g, '');
        return digits.slice(0, getPhoneMaxLength(countryCode));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchDeliveryCities = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/admin/delivery-settings`);
                if (Array.isArray(response.data)) {
                    const cities = response.data
                        .map(item => (item.city || '').trim())
                        .filter(city => city && city.toLowerCase() !== 'default')
                        .filter((city, index, self) => self.findIndex(c => c.toLowerCase() === city.toLowerCase()) === index);
                    setDeliveryCities(cities);
                }
            } catch (error) {
                console.error('Error fetching delivery cities:', error);
            }
        };
        fetchDeliveryCities();
    }, []);

    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!isLogin) {
            if (step === 1) {
                // Validate step 1 fields
                if (!formData.name || !formData.email || !formData.password) {
                    setError('All fields are required');
                    setLoading(false);
                    return;
                }
                // Proceed to step 2
                setStep(2);
                setLoading(false);
                return;
            } else if (step === 2) {
                // Validate step 2 fields
                if (!formData.phone_number || !formData.address1 || !formData.city?.trim() || !formData.street || !formData.building || !formData.floor) {
                    setError('Please fill in all required fields');
                    setLoading(false);
                    return;
                }
                // Submit signup
                try {
                    const normalizedPhone = formData.phone_number.startsWith('+')
                        ? formData.phone_number
                        : `${formData.country_code}${formData.phone_number}`;
                    const response = await axios.post(`${API_BASE_URL}/user/signup`, {
                        ...formData,
                        phone_number: normalizedPhone
                    });
                    login(response.data.user, response.data.token);
                    const origin = location.state?.from?.pathname || '/';
                    navigate(origin);
                } catch (err) {
                    setError(err.response?.data?.message || 'Something went wrong. Please try again.');
                } finally {
                    setLoading(false);
                }
            }
        } else {
            // Login logic
            try {
                const response = await axios.post(`${API_BASE_URL}/user/login`, formData);
                login(response.data.user, response.data.token);
                const origin = location.state?.from?.pathname || '/';
                navigate(origin);
            } catch (err) {
                setError(err.response?.data?.message || 'Something went wrong. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className={`auth-container ${(isLogin || step === 1) ? 'auth-step-1' : 'auth-step-2'}`}>
            <div className="auth-card">
                {isLogin && (
                    <div className="auth-header">
                        <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                        <p>{isLogin ? 'Please enter your details to login' : 'Join our community today'}</p>
                    </div>
                )}
                {!isLogin && step === 1 && (
                    <div className="auth-header">
                        <h2>Create Account</h2>
                        <p>Join our community today</p>
                    </div>
                )}

                {error && <div className={`auth-message ${error.includes('successful') ? 'success' : 'error'}`}>{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    {!isLogin && step === 1 && (
                        <>
                            <div className="form-group">
                                <label htmlFor="name">Full Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder="Enter your full name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                    >
                                        {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {!isLogin && step === 2 && (
                        <>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>

                            <div className="form-row form-row--phone">
                                <div className="form-group">
                                    <label>Country</label>
                                    <div ref={countryDropdownRef} style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="country-dropdown-button"
                                            onClick={() => setCountryDropdownOpen((prev) => !prev)}
                                        >
                                            <ReactCountryFlag
                                                svg
                                                countryCode={getCountryByCode(formData.country_code)?.iso || 'US'}
                                                style={{ width: '22px', height: '16px' }}
                                                title={getCountryByCode(formData.country_code)?.label || 'Country'}
                                            />
                                            <span>{formData.country_code}</span>
                                        </button>
                                        {isCountryDropdownOpen && (
                                            <div className="country-dropdown-list">
                                                {countryOptions.map((option) => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        className="country-dropdown-item"
                                                        onClick={() => {
                                                            setFormData({
                                                                ...formData,
                                                                country: option.label,
                                                                country_code: option.value,
                                                                phone_number: sanitizePhoneNumber(formData.phone_number, option.value)
                                                            });
                                                            setCountryDropdownOpen(false);
                                                        }}
                                                    >
                                                        <ReactCountryFlag
                                                            svg
                                                            countryCode={option.iso}
                                                            style={{ width: '20px', height: '15px' }}
                                                            title={option.label}
                                                        />
                                                        <span>{`${option.value} (${option.label})`}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone_number">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone_number"
                                        name="phone_number"
                                        placeholder={`Digits only. Max ${getPhoneMaxLength(formData.country_code)} digits.`}
                                        value={formData.phone_number}
                                        onChange={(e) => setFormData({ ...formData, phone_number: sanitizePhoneNumber(e.target.value, formData.country_code) })}
                                        required
                                    />

                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="city">City</label>
                                    {deliveryCities.length > 0 ? (
                                        <select
                                            id="city"
                                            name="city"
                                            value={showOtherCityInput ? 'other' : formData.city}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === 'other') {
                                                    setShowOtherCityInput(true);
                                                    setFormData({ ...formData, city: '' });
                                                } else {
                                                    setShowOtherCityInput(false);
                                                    setFormData({ ...formData, city: value });
                                                }
                                            }}
                                            required
                                        >
                                            <option value="">Select city</option>
                                            {deliveryCities.map((city) => (
                                                <option key={city} value={city}>{city}</option>
                                            ))}
                                            <option value="other">Other</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            required
                                        />
                                    )}
                                </div>
                                <div className="form-group">
                                    {showOtherCityInput && (
                                        <>
                                            <label htmlFor="other-city">Type City</label>
                                            <input
                                                type="text"
                                                id="other-city"
                                                name="city"
                                                placeholder="Enter city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="building">Building</label>
                                    <input
                                        type="text"
                                        id="building"
                                        name="building"
                                        value={formData.building}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="floor">Floor</label>
                                    <input
                                        type="text"
                                        id="floor"
                                        name="floor"
                                        value={formData.floor}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row form-row--three">
                                <div className="form-group">
                                    <label htmlFor="address1">Address Line 1</label>
                                    <input
                                        type="text"
                                        id="address1"
                                        name="address1"
                                        value={formData.address1}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="address2">Address Line 2 (Optional)</label>
                                    <input
                                        type="text"
                                        id="address2"
                                        name="address2"
                                        value={formData.address2}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="street">Street</label>
                                    <input
                                        type="text"
                                        id="street"
                                        name="street"
                                        placeholder="Main St"
                                        value={formData.street}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                        </>
                    )}

                    {isLogin && (
                        <>
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                    >
                                        {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <button type="submit" className="auth-button" disabled={loading}>
                        {loading ? 'Processing...' : (isLogin ? 'Sign In' : (step === 1 ? 'Continue' : 'Sign Up'))}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>
                        {isLogin ? "Don't have an account? " : (step === 1 ? "Already have an account? " : "Step 1 of 2 - ")}
                        <span onClick={() => {
                            setIsLogin(!isLogin);
                            setStep(1);
                            setError('');
                            setShowOtherCityInput(false);
                            setFormData({
                                name: '',
                                email: '',
                                password: '',
                                country: 'Lebanon',
                                country_code: '+961',
                                city: '',
                                phone_number: '',
                                address1: '',
                                address2: '',
                                street: '',
                                building: '',
                                floor: '',
                            });
                        }}>
                            {isLogin ? 'Sign Up' : (step === 1 ? 'Login' : 'Back to Login')}
                        </span>
                    </p>
                    {!isLogin && step === 2 && (
                        <p>
                            <span onClick={() => { setStep(1); setError(''); setShowOtherCityInput(false); }}>
                                Back to Step 1
                            </span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Auth;
